<template>
  <Teleport :to="modal.element.value ?? 'body'" :disabled="!isEmbedded">
    <div
      ref="bar"
      :tabindex="isOpen ? 0 : -1"
      :class="['filters-bar', isOpen && 'filters-bar--open', isEmbedded && 'filters-bar--embedded']"
    >
      <Transition name="filters-bar">
        <div
          v-show="isOpen"
          v-on-click-outside="clickOutsideConfig"
          class="filters-bar__bar"
          @keydown.esc="onEscapeKey"
        >
          <div class="filters-bar__header">
            <h2 class="filters-bar__heading font-m">{{ header }}</h2>
            <button
              class="filters-bar__close-btn"
              aria-label="Close filters"
              @click="onCloseClick()"
            >
              <i class="icon icon-Close" aria-hidden="true"></i>
            </button>
          </div>
          <div class="filters-bar__content">
            <div v-for="set in availableFilters" :key="set.name" class="filters-bar__set">
              <base-accordion v-if="set.values.length" :label="set.name" initial-state="open">
                <div class="filters-bar__items">
                  <template v-if="set.type === 'switch'">
                    <base-switch
                      v-for="filter in set.values"
                      :key="filter.name"
                      :label="filter.name"
                      :model-value="filter.enabled"
                      @update:model-value="
                        (value) => {
                          value
                            ? enableFilter(set.name, filter.name)
                            : disableFilter(set.name, filter.name);
                        }
                      "
                    />
                  </template>
                  <template v-else>
                    <button
                      v-for="filter in set.values"
                      :key="filter.name"
                      class="filters-bar__filter btn btn--rounded"
                      :class="[`btn--${filter.enabled ? 'primary' : 'secondary'}`]"
                      :data-filter-id="`${set.name}/${filter.name}`"
                      tabindex="0"
                      :aria-label="`${filter.name} - ${filter.enabled ? 'selected' : 'unselected'}`"
                      :disabled="isQueryLoading"
                      @click="
                        !filter.enabled
                          ? enableFilter(set.name, filter.name)
                          : set.type === 'filters'
                          ? disableFilter(set.name, filter.name)
                          : null
                      "
                      @focus="onFilterFocus(set.name, filter.name)"
                    >
                      {{ filter.name }}
                      <div
                        v-if="filter.enabled && set.type === 'filters'"
                        class="filters-bar__filter-close"
                      >
                        <i class="icon icon-Close" aria-hidden="true"></i>
                      </div>
                    </button>
                  </template>
                </div>
              </base-accordion>
            </div>
          </div>
          <div class="filters-bar__btns">
            <BaseButton
              class="filters-bar__clear-all"
              variant="secondary"
              :label="clearBtn"
              @click="clearAllFilters"
            />
            <BaseButton
              class="filters-bar__show-results"
              :label="filterButtonLabel"
              :is-loading="isQueryLoading"
              @click="onCloseClick(true)"
            />
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { FiltersBarProps, FilterValue } from './types';
import type { ModalContext } from '../BaseModal/types';
import { ref, computed, watch, watchEffect, inject, onBeforeUnmount } from 'vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { vOnClickOutside } from '@vueuse/components';
import BaseAccordion from '../BaseAccordion/BaseAccordion.vue';
import BaseButton from '../BaseButton/BaseButton.vue';
import BaseSwitch from '../BaseSwitch/BaseSwitch.vue';
import { useAppStore } from '../../../stores/appStore';

const appStore = useAppStore();

const showResultsNumber = inject('showResultsNumber', ref(true));
const modal = inject<ModalContext>('modalContext', {
  element: ref(null),
  blockModalScroll: () => {},
  unblockModalScroll: () => {},
});
const isEmbedded = computed(() => !!modal.element.value);

const emit = defineEmits<{
  onClose: [{ registerEvent: boolean }];
  onFilterEnable: [{ set: string; filter: string }];
  onFilterDisable: [{ set: string; filter: string }];
  onFilterClear: [];
}>();

const props = withDefaults(defineProps<FiltersBarProps>(), {
  filters: () => [],
  isOpen: false,
  results: 0,
  header: 'Filter',
  clearBtn: 'Clear',
  isQueryLoading: false,
  availableFilters: () => ({
    shouldRenderFilter: () => true,
  }),
});

const clickOutsideConfig = (_event: PointerEvent) => {
  if (isEmbedded.value) return;
  if (props.isOpen) onCloseClick();
};

const lastFocusedElement = {
  set: null,
  filter: null,
};

const TRANSITION_TIMEOUT = 200;
const bar = ref(null);

const { activate, deactivate } = useFocusTrap(bar, {
  allowOutsideClick: true,
  escapeDeactivates: () => !isEmbedded.value,
});

watchEffect(() => {
  if (!isEmbedded.value) {
    appStore.setBackdrop('FiltersBar', { enable: props.isOpen, global: true });
  }
});

onBeforeUnmount(() => {
  if (!isEmbedded.value) {
    appStore.setBackdrop('FiltersBar', { enable: false, global: false });
  } else {
    modal.unblockModalScroll();
  }
});

const filterButtonLabel = computed(
  () =>
    `Show ${showResultsNumber?.value ? props.results : ''} result${props.results === 1 ? '' : 's'}`
);

const availableFilters = computed(() =>
  props.filters
    .map((section) => ({
      ...section,
      values: section.values.filter(
        (filter: FilterValue) =>
          props.availableFilters?.shouldRenderFilter?.(filter, section.name.toLowerCase()) ||
          filter.enabled
      ),
    }))
    .filter((section) => section?.values?.length)
);

const openFilterBar = (): void => {
  if (!isEmbedded.value) appStore.blockScrolling();
  else modal.blockModalScroll();
  setTimeout(() => activate(), TRANSITION_TIMEOUT);
};

const onEscapeKey = (event: KeyboardEvent): void => {
  if (isEmbedded.value) event.stopPropagation();
  onCloseClick();
};

const onCloseClick = (registerEvent: boolean = false): void => {
  emit('onClose', { registerEvent });
};

const closeFilterBar = (): void => {
  lastFocusedElement.set = null;
  lastFocusedElement.filter = null;

  if (!isEmbedded.value) appStore.unblockScrolling();
  else modal.unblockModalScroll();
  setTimeout(() => deactivate(), TRANSITION_TIMEOUT);
};

const enableFilter = (set: string, filter: string): void => {
  emit('onFilterEnable', { set, filter });
};

const disableFilter = (set: string, filter: string): void => {
  emit('onFilterDisable', { set, filter });
};

const clearAllFilters = (): void => {
  emit('onFilterClear');
};

const onFilterFocus = (set: string, filter: string): void => {
  lastFocusedElement.set = set;
  lastFocusedElement.filter = filter;
};

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) openFilterBar();
    else closeFilterBar();
  }
);

watch(
  () => props.filters,
  () => {
    if (!lastFocusedElement.filter) return;
    const focusedFilter = bar.value?.querySelector(
      `[data-filter-id="${lastFocusedElement.set}/${lastFocusedElement.filter}"]`
    );
    if (focusedFilter)
      setTimeout(() => {
        focusedFilter.focus();
      }, 0);
  }
);
</script>

<style lang="scss">
@import './scss/FiltersBar.scss';
</style>
